import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, Fade, Grid, Stack, Typography, useTheme } from '@mui/material';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import type { AccountData } from '../../api/models/AccountData';
import type { PassOrderPageAvailabilityOut } from '../../api/models/PassOrderPageAvailabilityOut';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import type { PassScheduleSelectionDTO } from '../../api/models/PassScheduleSelectionDTO';
import type { SlotAvailabilityOut } from '../../api/models/SlotAvailabilityOut';
import { getTimeFormat } from '../../utils/getDateAndTimeFormat';
import { useLanguage } from '../../utils/GeneralUtils';
import { isSelected, isSlotDisabled, resolveRedeemablePages, toggleSelection, weekdayLabel, WEEKDAYS } from './utils';

type Props = {
  passOrderPage: PassOrderPageData;
  account: AccountData;
  value: PassScheduleSelectionDTO[];
  onChange: (next: PassScheduleSelectionDTO[]) => void;
  maxSelections?: number;
  availability?: PassOrderPageAvailabilityOut[];
  troubleshoot?: boolean;
};

type TroubleshootTarget = {
  orderPageId: string;
  weekday: string;
  startTime: number;
  slot: SlotAvailabilityOut;
};

export const PassScheduleSelector = ({
  passOrderPage,
  account,
  value,
  onChange,
  maxSelections,
  availability,
  troubleshoot,
}: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const timeFormat = getTimeFormat(account);
  const themedPrimary = theme.customTheme?.palette?.primary ?? theme.palette.primary.main;
  const themedContrast = theme.customTheme?.palette?.text?.button?.contained ?? theme.palette.primary.contrastText;
  const fullyBookedBg = theme.customTheme?.palette?.calendarPicker?.fullyBookedDay?.background ?? '#9E9E9E';
  const fullyBookedText = theme.customTheme?.palette?.text?.primary ?? theme.palette.text.primary;

  const resolved = useMemo(() => resolveRedeemablePages(passOrderPage, account), [passOrderPage, account]);
  const isMultiPage = resolved.length > 1;

  const [troubleshootTarget, setTroubleshootTarget] = useState<TroubleshootTarget | null>(null);

  const findSlot = (orderPageId: string, weekday: string, startTime: number): SlotAvailabilityOut | undefined => {
    const page = availability?.find((a) => a.order_page_id === orderPageId);
    const day = page?.availability.find((w) => w.weekday === weekday);
    return day?.slots.find((s) => s.startTime === startTime);
  };

  const hasAnyDisabledSlot = useMemo(
    () =>
      !!availability &&
      resolved.some(({ orderPageId, weeklyOptions }) =>
        Object.entries(weeklyOptions).some(([weekday, times]) =>
          times.some((startTime) => isSlotDisabled(availability, orderPageId, weekday, startTime)),
        ),
      ),
    [availability, resolved],
  );

  if (resolved.length === 0) {
    return null;
  }

  const renderGrid = (orderPageId: string, weeklyOptions: Record<string, number[]>) => (
    <Grid container spacing={1} wrap="wrap">
      {WEEKDAYS.map((weekday) => {
        const times = weeklyOptions[weekday];
        if (!times || times.length === 0) return null;
        return (
          <Grid item xs={12} sm={6} md={4} key={`${orderPageId}-${weekday}`}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
              }}>
              <Typography variant="subtitle2" fontWeight={600} align="center" sx={{ mb: 1 }}>
                {weekdayLabel(t, weekday)}
              </Typography>
              <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={0.75}>
                {times.map((startTime) => {
                  const selected = isSelected(value, orderPageId, weekday, startTime);
                  const atCap = maxSelections != null && value.length >= maxSelections;
                  const slotFull = isSlotDisabled(availability, orderPageId, weekday, startTime);
                  const chipDisabled = slotFull && !selected;
                  const clickDisabled = (chipDisabled && !troubleshoot) || (!selected && atCap);
                  const timeLabel = moment.utc(startTime * 1000).format(timeFormat);
                  const isTroubleshootActive =
                    !!troubleshoot &&
                    !!troubleshootTarget &&
                    troubleshootTarget.orderPageId === orderPageId &&
                    troubleshootTarget.weekday === weekday &&
                    troubleshootTarget.startTime === startTime;
                  const disabledSx = {
                    backgroundColor: fullyBookedBg,
                    color: fullyBookedText,
                    borderColor: fullyBookedBg,
                    textDecoration: 'line-through',
                    ...(isTroubleshootActive
                      ? { outline: '2px solid', outlineColor: 'warning.main', outlineOffset: 2 }
                      : {}),
                    '&.Mui-disabled': {
                      backgroundColor: fullyBookedBg,
                      color: fullyBookedText,
                      borderColor: fullyBookedBg,
                      opacity: 1,
                    },
                  } as const;
                  return (
                    <Chip
                      key={startTime}
                      label={timeLabel}
                      variant={selected ? 'filled' : 'outlined'}
                      aria-pressed={selected}
                      aria-disabled={chipDisabled && !troubleshoot}
                      disabled={chipDisabled && !troubleshoot}
                      onClick={() => {
                        if (chipDisabled && troubleshoot) {
                          const slot = findSlot(orderPageId, weekday, startTime);
                          if (slot) setTroubleshootTarget({ orderPageId, weekday, startTime, slot });
                          return;
                        }
                        if (clickDisabled) return;
                        setTroubleshootTarget(null);
                        onChange(toggleSelection(value, orderPageId, weekday, startTime));
                      }}
                      sx={
                        chipDisabled
                          ? disabledSx
                          : selected
                          ? {
                              backgroundColor: themedPrimary,
                              color: themedContrast,
                              borderColor: themedPrimary,
                              '&:hover': { backgroundColor: themedPrimary, opacity: 0.9 },
                            }
                          : {
                              borderColor: themedPrimary,
                              color: themedPrimary,
                            }
                      }
                    />
                  );
                })}
              </Stack>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Stack gap={2}>
      {resolved.map(({ orderPageId, page, weeklyOptions }) => (
        <Box key={orderPageId}>
          {isMultiPage && (
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              {page.label?.[language] || ''}
            </Typography>
          )}
          {renderGrid(orderPageId, weeklyOptions)}
        </Box>
      ))}
      {hasAnyDisabledSlot && (
        <Stack direction="row" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.5,
              backgroundColor: fullyBookedBg,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('pass_schedule.fullyBookedLegend')}
          </Typography>
        </Stack>
      )}
      {troubleshoot && troubleshootTarget && (
        <Fade in timeout={600}>
          <Alert severity="warning">
            <Grid container flexDirection="column" gap={0.5}>
              <Typography variant="body2">
                {t('pass_schedule.troubleshoot.title', {
                  weekday: weekdayLabel(t, troubleshootTarget.weekday),
                  time: moment.utc(troubleshootTarget.startTime * 1000).format(timeFormat),
                })}
              </Typography>
              <Typography variant="body2">
                {t('pass_schedule.troubleshoot.capacity', {
                  holders: troubleshootTarget.slot.holdersCount,
                  capacity: troubleshootTarget.slot.capacity,
                })}
              </Typography>
              {!isEmpty(troubleshootTarget.slot.holderOrderNumbers) && (
                <Grid container gap={1}>
                  <Typography variant="body2">{t('pass_schedule.troubleshoot.orderNumbers')}</Typography>
                  {troubleshootTarget.slot.holderOrderNumbers?.map((n) => (
                    <Typography key={n} variant="body2">
                      {n}
                    </Typography>
                  ))}
                </Grid>
              )}
            </Grid>
          </Alert>
        </Fade>
      )}
    </Stack>
  );
};

export default PassScheduleSelector;
